import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Spinner } from 'react-bootstrap';
import { CreditCard, Calendar } from 'lucide-react';
import { api } from '../../utils/apiClient';

const PaymentsPage = () => {
  const { t, i18n } = useTranslation();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsSection('account');
        if (!cancelled) setAccount(data?.account || {});
      } catch {
        if (!cancelled) setAccount({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const status = account?.subscriptionStatus || 'trial';
  const trialDays = Number.isFinite(Number(account?.trialDaysRemaining))
    ? Math.max(0, Number(account.trialDaysRemaining))
    : 0;
  const trialStartedAt = account?.trialStartedAt
    ? new Date(account.trialStartedAt).toLocaleDateString(i18n.language)
    : null;

  return (
    <div className="py-3 px-2">
      <div className="mb-4">
        <h2 className="h4 mb-1">{t('account.payments.title')}</h2>
        <p className="text-muted mb-0">{t('account.payments.subtitle')}</p>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <CreditCard size={20} style={{ color: '#022a6e' }} />
                <span className="fw-semibold">{t('account.payments.subscriptionStatus')}</span>
              </div>
              <Badge bg={status === 'active' ? 'success' : status === 'expired' ? 'danger' : 'warning'} text={status === 'active' ? 'white' : 'dark'} className="text-uppercase mb-2">
                {t(`account.payments.statuses.${status}`, { defaultValue: status })}
              </Badge>
              {status === 'trial' && (
                <p className="text-muted mb-0 mt-2">{t('account.payments.trialInfo')}</p>
              )}
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Calendar size={20} style={{ color: '#022a6e' }} />
                <span className="fw-semibold">{t('account.payments.trialRemaining')}</span>
              </div>
              <div className="fs-3 fw-bold" style={{ color: '#022a6e' }}>
                {status === 'active' ? '—' : t('account.payments.daysValue', { count: trialDays })}
              </div>
              <p className="text-muted small mb-0 mt-2">
                {status === 'active'
                  ? t('account.payments.activeMsg')
                  : status === 'expired'
                    ? t('account.payments.expiredMsg')
                    : trialStartedAt
                      ? t('account.payments.startedMsg', { date: trialStartedAt })
                      : t('account.payments.upgradeMsg')}
              </p>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Card className="border-0 shadow-sm mt-3">
        <Card.Body className="text-muted">
          {t('account.payments.footer')}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaymentsPage;
